import React, { useState, useEffect, useMemo } from 'react';
import { DateRange } from 'Components/DateRange';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
  Paper,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import DirectionsWalkOutlinedIcon from '@mui/icons-material/DirectionsWalkOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { filterFilledReviewSections, stripHtml } from './visitConcepts';

const TRUNCATE_LEN = 60;

function getOrdinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function formatDate(val) {
  if (!val || val === 'Unknown Date') return '—';
  if (typeof val === 'string' && val.includes('-')) {
    const parts = val.split('-');
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        // YYYY-MM-DD -> DD-MM-YYYY
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return val;
    }
  }
  return String(val);
}

function formatDateTime(val) {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      return d.toLocaleString([], {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  } catch (e) {}
  return String(val);
}

function getVisitTimestamp(r) {
  if (r.visit_date) {
    const timeStr = r.visit_time ? (r.visit_time.length === 5 ? `${r.visit_time}:00` : r.visit_time) : '00:00:00';
    const t = new Date(`${r.visit_date}T${timeStr}`).getTime();
    if (!isNaN(t)) return t;
  }
  if (r.checked_in_at) {
    const t = new Date(r.checked_in_at).getTime();
    if (!isNaN(t)) return t;
  }
  if (r.created) {
    const t = new Date(r.created).getTime();
    if (!isNaN(t)) return t;
  }
  return Number(r.id || 0);
}

const getStatusMeta = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'visited') {
    return {
      label: 'Visited',
      bg: '#dcfce7',
      color: '#15803d',
      border: '#bbf7d0',
    };
  }
  if (s === 'pending') {
    return {
      label: 'Pending',
      bg: '#fef3c7',
      color: '#b45309',
      border: '#fde68a',
    };
  }
  if (s === 'postponed') {
    return {
      label: 'Postponed',
      bg: '#e0f2fe',
      color: '#0369a1',
      border: '#bae6fd',
    };
  }
  if (s === 'cancel' || s === 'canceled' || s === 'cancelled') {
    return {
      label: 'Canceled',
      bg: '#fee2e2',
      color: '#b91c1c',
      border: '#fecaca',
    };
  }
  return {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending',
    bg: '#f1f5f9',
    color: '#475569',
    border: '#cbd5e1',
  };
};

const getVisitBadgeGradient = (num) => {
  switch (num) {
    case 1:
      return 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)'; // 1st: Sapphire Blue
    case 2:
      return 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)'; // 2nd: Royal Purple
    case 3:
      return 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)'; // 3rd: Teal Emerald
    case 4:
      return 'linear-gradient(135deg, #c2410c 0%, #f97316 100%)'; // 4th: Amber
    case 5:
      return 'linear-gradient(135deg, #be185d 0%, #ec4899 100%)'; // 5th: Pink
    default:
      return 'linear-gradient(135deg, #334155 0%, #64748b 100%)'; // 6th+: Slate
  }
};

function ExpandableCell({ value, isHtml = false }) {
  const [expanded, setExpanded] = React.useState(false);

  const displayValue = isHtml ? filterFilledReviewSections(value) : String(value || '').trim();
  if (!displayValue) return '-';

  const plainText = isHtml ? stripHtml(displayValue) : displayValue;
  const needsMore = plainText.length > TRUNCATE_LEN;

  const handleToggle = (e) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const htmlContentSx = {
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: 'text.primary',
    '& strong': { fontWeight: 700 },
    '& b': { fontWeight: 700 },
    '& p': { margin: '0 0 8px' },
    '& ul, & ol': { paddingLeft: 3, margin: '6px 0' },
  };

  if (!needsMore) {
    if (isHtml) {
      return (
        <Box sx={{ py: 0.5, width: '100%', ...htmlContentSx }} dangerouslySetInnerHTML={{ __html: displayValue }} />
      );
    }
    return (
      <Typography variant="body2" sx={{ py: 0.5, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
        {displayValue}
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 0.5, width: '100%' }}>
      {expanded ? (
        isHtml ? (
          <Box sx={htmlContentSx} dangerouslySetInnerHTML={{ __html: displayValue }} />
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {displayValue}
          </Typography>
        )
      ) : (
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          {`${plainText.slice(0, TRUNCATE_LEN)}...`}
        </Typography>
      )}
      <Button
        size="small"
        sx={{ p: 0, minWidth: 'auto', textTransform: 'none', mt: 0.5, fontWeight: 700 }}
        onClick={handleToggle}
      >
        {expanded ? 'Show less' : 'Read full feedback'}
      </Button>
    </Box>
  );
}

function VisitSummaryView({ rows, loading }) {
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' = 1st Visit first, 'desc' = Latest Visit first
  const [viewMode, setViewMode] = useState('table'); // 'table' (Row-wise list 1,2,3) or 'cards'
  const [selectedVisitIndex, setSelectedVisitIndex] = useState(null);

  // 1. Process and assign canonical chronological order per company
  const processedVisits = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const formatTime = (isoStr) => {
      if (!isoStr) return 'N/A';
      return new Date(isoStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const calcDuration = (start, end) => {
      if (!start || !end) return 'N/A';
      const diffMs = new Date(end).getTime() - new Date(start).getTime();
      if (isNaN(diffMs) || diffMs < 0) return 'N/A';
      const hrs = Math.floor(diffMs / 3600000);
      const mins = Math.floor((diffMs % 3600000) / 60000);
      return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    };

    // Sort strictly chronological (earliest visit first)
    const sorted = [...rows].sort((a, b) => {
      const diff = getVisitTimestamp(a) - getVisitTimestamp(b);
      if (diff !== 0) return diff;
      return (Number(a.id) || 0) - (Number(b.id) || 0);
    });

    const companyVisitsCounter = {};

    return sorted.map((r, index) => {
      const compKey = r.company || r.company_name || 'default';
      if (!companyVisitsCounter[compKey]) {
        companyVisitsCounter[compKey] = 0;
      }
      companyVisitsCounter[compKey] += 1;
      const visitNum = companyVisitsCounter[compKey];
      const ordinal = `${getOrdinal(visitNum)} Visit`;

      const inTime = formatTime(r.checked_in_at);
      const outTime = formatTime(r.checked_out_at);
      const duration = calcDuration(r.checked_in_at, r.checked_out_at);
      const statusText = r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1) : 'Pending';

      return {
        ...r,
        id: r.id || index,
        visitNumber: visitNum,
        visitOrdinal: ordinal,
        date: r.visit_date || 'Unknown Date',
        company: r.company_name || 'Unknown Company',
        companyAddress: r.company_address || '',
        user: r.assigned_to_name || 'Unassigned',
        status: statusText,
        inTime,
        outTime,
        duration,
        description: r.description || '',
        review: r.school_review || '',
        concepts: Array.isArray(r.concepts_explained) ? r.concepts_explained : [],
        conceptsToExplain: Array.isArray(r.concepts_to_explain) ? r.concepts_to_explain : [],
        media: Array.isArray(r.media) ? r.media : [],
        priority: r.priority || 'medium',
        visitType: r.visit_type || '',
        postponedDate: r.postponed_date || '',
        postponedTime: r.postponed_time || '',
        created: r.created || '',
        modified: r.modified || '',
      };
    });
  }, [rows]);

  // 2. Order for display
  const displayVisits = useMemo(() => {
    const list = [...processedVisits];
    if (sortOrder === 'desc') {
      list.reverse();
    }
    return list;
  }, [processedVisits, sortOrder]);

  // Keyboard navigation for Previous/Next in Dialog
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedVisitIndex === null) return;
      if (e.key === 'ArrowLeft') {
        setSelectedVisitIndex((i) => (i !== null && i > 0 ? i - 1 : i));
      } else if (e.key === 'ArrowRight') {
        setSelectedVisitIndex((i) => (i !== null && i < displayVisits.length - 1 ? i + 1 : i));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedVisitIndex, displayVisits.length]);

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Loading visit summary…</Typography>
      </Box>
    );
  }

  if (processedVisits.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No visits found in this date range.</Typography>
      </Box>
    );
  }

  const totalCount = processedVisits.length;
  const visitedCount = processedVisits.filter((v) => v.status?.toLowerCase() === 'visited').length;
  const pendingCount = processedVisits.filter((v) => v.status?.toLowerCase() === 'pending').length;

  const currentVisit = selectedVisitIndex !== null ? displayVisits[selectedVisitIndex] : null;

  return (
    <Box>
      {/* 🎯 SPACIOUS TOP SUMMARY & CONTROLS HEADER */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3.5,
          borderRadius: 3.5,
          border: '1px solid #e2e8f0',
          background: 'linear-gradient(to right, #ffffff, #f8fafc)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
        }}
      >
        {/* TOP ROW: Title & Summary Statistics Badges */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                bgcolor: '#eff6ff',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 10px rgba(37,99,235,0.12)',
              }}
            >
              <DirectionsWalkOutlinedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                Visits Timeline & History
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                Row-wise sequential log of visits · Click any visit row to inspect complete details
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Chip
              label={`${totalCount} Total ${totalCount === 1 ? 'Visit' : 'Visits'}`}
              sx={{ fontWeight: 700, px: 1, py: 0.5, bgcolor: '#f1f5f9', color: '#334155' }}
            />
            {visitedCount > 0 && (
              <Chip
                icon={<CheckCircleOutlineIcon style={{ fontSize: '0.95rem', color: '#15803d' }} />}
                label={`${visitedCount} Visited`}
                sx={{ fontWeight: 700, px: 1, py: 0.5, bgcolor: '#dcfce7', color: '#15803d' }}
              />
            )}
            {pendingCount > 0 && (
              <Chip
                icon={<HourglassEmptyOutlinedIcon style={{ fontSize: '0.95rem', color: '#b45309' }} />}
                label={`${pendingCount} Pending`}
                sx={{ fontWeight: 700, px: 1, py: 0.5, bgcolor: '#fef3c7', color: '#b45309' }}
              />
            )}
          </Stack>
        </Box>

        <Divider sx={{ my: 2.5, borderColor: '#e2e8f0' }} />

        {/* BOTTOM ROW: View Mode Toggle & Sort Order Selector */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {/* VIEW MODE TOGGLE */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(e, next) => {
              if (next) setViewMode(next);
            }}
            sx={{
              bgcolor: '#ffffff',
              width: 'fit-content !important',
              display: 'inline-flex !important',
              flexShrink: 0,
            }}
          >
            <ToggleButton value="table" sx={{ px: 2, py: 0.75, fontWeight: 700, textTransform: 'none' }}>
              <ViewListOutlinedIcon sx={{ mr: 1, fontSize: 19 }} /> Row List (1, 2, 3)
            </ToggleButton>
            <ToggleButton value="cards" sx={{ px: 2, py: 0.75, fontWeight: 700, textTransform: 'none' }}>
              <GridViewOutlinedIcon sx={{ mr: 1, fontSize: 19 }} /> Cards View
            </ToggleButton>
          </ToggleButtonGroup>

          {/* SORT ORDER SELECTOR */}
          <FormControl size="small" sx={{ minWidth: 240, width: 'auto', flexShrink: 0 }}>
            <InputLabel>Order</InputLabel>
            <Select
              value={sortOrder}
              label="Order"
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <MenuItem value="asc">1st Visit → Latest (In Order)</MenuItem>
              <MenuItem value="desc">Latest → 1st Visit</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* 🎯 CLEAN, UNCLUTTERED ROW-WISE LIST (TABLE VIEW) */}
      {viewMode === 'table' ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 3.5,
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
          }}
        >
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow sx={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <TableCell sx={{ fontWeight: 800, color: '#334155', width: 140 }}># Order</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#334155' }}>Visit Date & Time</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#334155' }}>Visit Purpose</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#334155' }}>Assigned Staff</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#334155' }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 800, color: '#334155', width: 170 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayVisits.map((row, index) => {
                const statusMeta = getStatusMeta(row.status);

                return (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => setSelectedVisitIndex(index)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: '#f1f5f9',
                      },
                      borderBottom: '1px solid #f1f5f9',
                    }}
                  >
                    {/* # ORDER WITH PILL BADGE */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            background: getVisitBadgeGradient(row.visitNumber),
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 800,
                            fontSize: '0.82rem',
                            flexShrink: 0,
                          }}
                        >
                          {row.visitNumber}
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#1e293b', whiteSpace: 'nowrap' }}>
                          {row.visitOrdinal}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* DATE & SCHEDULED TIME */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: '#64748b' }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {formatDate(row.date)}
                        </Typography>
                      </Box>
                      {row.visit_time && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                          Scheduled: {row.visit_time}
                        </Typography>
                      )}
                    </TableCell>

                    {/* PURPOSE / TYPE & PRIORITY */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                          {row.visitType || 'Routine Visit'}
                        </Typography>
                        {row.priority && row.priority !== 'medium' && (
                          <Chip
                            label={row.priority}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              textTransform: 'capitalize',
                              bgcolor: row.priority === 'high' ? '#fee2e2' : '#f1f5f9',
                              color: row.priority === 'high' ? '#b91c1c' : '#475569',
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* ASSIGNED STAFF */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <PersonOutlineIcon sx={{ fontSize: 16, color: '#64748b' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                          {row.user}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* STATUS CHIP */}
                    <TableCell>
                      <Chip
                        label={statusMeta.label}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: statusMeta.bg,
                          color: statusMeta.color,
                          border: `1px solid ${statusMeta.border}`,
                        }}
                      />
                    </TableCell>

                    {/* ACTION BUTTON */}
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedVisitIndex(index);
                        }}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.78rem',
                          px: 1.75,
                          bgcolor: '#2563eb',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: '#1d4ed8',
                            boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
                          },
                        }}
                      >
                        View Full Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* 🎯 CARDS VIEW */
        <Grid container spacing={3}>
          {displayVisits.map((row, index) => {
            const statusMeta = getStatusMeta(row.status);

            return (
              <Grid item xs={12} md={6} lg={6} key={row.id}>
                <Card
                  elevation={0}
                  onClick={() => setSelectedVisitIndex(index)}
                  sx={{
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      borderColor: '#cbd5e1',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                    {/* CARD HEADER */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            background: getVisitBadgeGradient(row.visitNumber),
                            color: '#ffffff',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                            flexShrink: 0,
                          }}
                        >
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', lineHeight: 1 }}>
                            {getOrdinal(row.visitNumber)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, lineHeight: 1, mt: 0.25 }}>
                            Visit
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#0f172a', lineHeight: 1.2 }}>
                            {row.visitOrdinal}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                            {row.company} {row.visitType ? `• ${row.visitType}` : ''}
                          </Typography>
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          icon={<CalendarTodayOutlinedIcon style={{ fontSize: '0.8rem' }} />}
                          label={formatDate(row.date)}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 700, borderColor: '#cbd5e1', bgcolor: '#f8fafc', color: '#334155' }}
                        />
                        <Chip
                          label={statusMeta.label}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor: statusMeta.bg,
                            color: statusMeta.color,
                            border: `1px solid ${statusMeta.border}`,
                          }}
                        />
                      </Stack>
                    </Box>

                    {/* ASSIGNED STAFF & PURPOSE */}
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, p: 1.25, bgcolor: '#f8fafc', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonOutlineIcon sx={{ fontSize: 18, color: '#64748b' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', lineHeight: 1 }}>
                            Assigned Staff
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            {row.user}
                          </Typography>
                        </Box>
                      </Box>

                      {row.visit_time && (
                        <>
                          <Divider orientation="vertical" flexItem />
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', lineHeight: 1 }}>
                              Scheduled
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                              {row.visit_time}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Stack>

                    {/* VIEW DETAILS ACTION */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                      >
                        Inspect Full Details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* 🎯 COMPREHENSIVE VISIT DETAILS MODAL WITH DEEP-DIVE INFORMATION & PREV/NEXT */}
      <Dialog
        open={selectedVisitIndex !== null}
        onClose={() => setSelectedVisitIndex(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3.5,
            boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
          },
        }}
      >
        {currentVisit && (
          <>
            {/* ── DIALOG HEADER ── */}
            <DialogTitle
              sx={{
                p: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #e2e8f0',
                background: 'linear-gradient(to right, #f8fafc, #ffffff)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    background: getVisitBadgeGradient(currentVisit.visitNumber),
                    color: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', lineHeight: 1 }}>
                    {getOrdinal(currentVisit.visitNumber)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, lineHeight: 1, mt: 0.25 }}>
                    Visit
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                    {currentVisit.visitOrdinal} — Full Visit Dossier
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    {currentVisit.company} {currentVisit.companyAddress ? `• ${currentVisit.companyAddress}` : ''}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip
                  label={getStatusMeta(currentVisit.status).label}
                  sx={{
                    fontWeight: 800,
                    bgcolor: getStatusMeta(currentVisit.status).bg,
                    color: getStatusMeta(currentVisit.status).color,
                    border: `1px solid ${getStatusMeta(currentVisit.status).border}`,
                  }}
                />
                <IconButton onClick={() => setSelectedVisitIndex(null)} size="small" sx={{ color: '#64748b' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            {/* ── STICKY PREVIOUS & NEXT NAVIGATION TOOLBAR ── */}
            <Box
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: '#f1f5f9',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIosNewIcon sx={{ fontSize: '13px !important' }} />}
                disabled={selectedVisitIndex <= 0}
                onClick={() => setSelectedVisitIndex((i) => Math.max(0, i - 1))}
                sx={{
                  bgcolor: '#ffffff',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  borderColor: '#cbd5e1',
                  color: '#1e293b',
                  '&:hover': { bgcolor: '#ffffff', borderColor: '#94a3b8' },
                  '&.Mui-disabled': { bgcolor: '#f8fafc', color: '#94a3b8' },
                }}
              >
                Previous Visit {selectedVisitIndex > 0 ? `(${displayVisits[selectedVisitIndex - 1]?.visitOrdinal})` : ''}
              </Button>

              <Typography variant="body2" sx={{ fontWeight: 800, color: '#334155' }}>
                Visit {selectedVisitIndex + 1} of {displayVisits.length} ({currentVisit.visitOrdinal})
              </Typography>

              <Button
                variant="outlined"
                size="small"
                endIcon={<ArrowForwardIosIcon sx={{ fontSize: '13px !important' }} />}
                disabled={selectedVisitIndex >= displayVisits.length - 1}
                onClick={() => setSelectedVisitIndex((i) => Math.min(displayVisits.length - 1, i + 1))}
                sx={{
                  bgcolor: '#ffffff',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  borderColor: '#cbd5e1',
                  color: '#1e293b',
                  '&:hover': { bgcolor: '#ffffff', borderColor: '#94a3b8' },
                  '&.Mui-disabled': { bgcolor: '#f8fafc', color: '#94a3b8' },
                }}
              >
                Next Visit {selectedVisitIndex < displayVisits.length - 1 ? `(${displayVisits[selectedVisitIndex + 1]?.visitOrdinal})` : ''}
              </Button>
            </Box>

            {/* ── DIALOG DEEP DIVE CONTENT ── */}
            <DialogContent sx={{ p: 3 }}>
              {/* 1. VISIT MESSAGE & INSTRUCTIONS (PROMINENTLY HIGHLIGHTED) */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  bgcolor: '#f0f9ff',
                  border: '1.5px solid #bae6fd',
                  borderRadius: 3,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0369a1', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} /> Visit Message, Purpose & Description
                </Typography>
                {currentVisit.description ? (
                  <Typography variant="body1" sx={{ color: '#0f172a', fontWeight: 600, whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '0.98rem' }}>
                    {currentVisit.description}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                    No specific description message was added for this visit.
                  </Typography>
                )}
              </Paper>

              {/* 2. KEY METADATA SUMMARY GRID */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, bgcolor: '#f8fafc' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                      Scheduled Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      {formatDate(currentVisit.date)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, bgcolor: '#f8fafc' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                      Assigned Staff
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      {currentVisit.user}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, bgcolor: '#f8fafc' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                      Visit Type / Goal
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      {currentVisit.visitType || 'Routine Visit'}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, bgcolor: '#f8fafc' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                      Time & Priority
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#0f172a', textTransform: 'capitalize' }}>
                      {currentVisit.visit_time || '—'} {currentVisit.priority ? `(${currentVisit.priority})` : ''}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* 3. ATTENDANCE & CHECK-IN TRACKER */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 3,
                  bgcolor: '#ffffff',
                  borderColor: '#e2e8f0',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#334155', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AccessTimeOutlinedIcon sx={{ fontSize: 18, color: '#2563eb' }} /> Attendance, Check-in & Time Tracking
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 1.75, borderRadius: 2.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                      <Typography variant="caption" sx={{ color: '#166534', fontWeight: 700, display: 'block' }}>
                        Check-in Time
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#15803d', mt: 0.5 }}>
                        {currentVisit.inTime !== 'N/A' ? `🟢 ${currentVisit.inTime}` : 'Not Checked-in'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 1.75, borderRadius: 2.5, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                      <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 700, display: 'block' }}>
                        Check-out Time
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0284c7', mt: 0.5 }}>
                        {currentVisit.outTime !== 'N/A' ? `🔵 ${currentVisit.outTime}` : 'Not Checked-out'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 1.75, borderRadius: 2.5, bgcolor: '#faf5ff', border: '1px solid #e9d5ff' }}>
                      <Typography variant="caption" sx={{ color: '#6b21a8', fontWeight: 700, display: 'block' }}>
                        Time Spent Duration
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#7c3aed', mt: 0.5 }}>
                        {currentVisit.duration !== 'N/A' ? `⏱ ${currentVisit.duration}` : '—'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* 4. CONCEPTS PLANNED VS EXPLAINED CHECKLIST */}
              {(currentVisit.conceptsToExplain?.length > 0 || currentVisit.concepts?.length > 0) && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 3,
                    bgcolor: '#ffffff',
                    borderColor: '#e2e8f0',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#334155', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ListAltOutlinedIcon sx={{ fontSize: 18, color: '#4f46e5' }} /> Concepts Checklist & Demonstration Status
                  </Typography>

                  <Grid container spacing={3}>
                    {/* PLANNED CONCEPTS */}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569', display: 'block', mb: 1 }}>
                        Planned to Explain ({currentVisit.conceptsToExplain?.length || 0})
                      </Typography>
                      {currentVisit.conceptsToExplain?.length > 0 ? (
                        <Stack spacing={1}>
                          {currentVisit.conceptsToExplain.map((c, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                              <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: '#64748b' }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                {c}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          No specific concepts were planned.
                        </Typography>
                      )}
                    </Grid>

                    {/* COMPLETED / EXPLAINED CONCEPTS */}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#166534', display: 'block', mb: 1 }}>
                        Actually Explained / Delivered ({currentVisit.concepts?.length || 0})
                      </Typography>
                      {currentVisit.concepts?.length > 0 ? (
                        <Stack spacing={1}>
                          {currentVisit.concepts.map((c, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                              <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#166534' }}>
                                {c}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          No concepts marked as explained yet.
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* 5. SCHOOL REVIEW & FIELD FEEDBACK */}
              {currentVisit.review ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 3,
                    bgcolor: '#fafafa',
                    borderColor: '#e2e8f0',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#334155', display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <RateReviewOutlinedIcon sx={{ fontSize: 18, color: '#4f46e5' }} /> Visit Review & School Field Feedback
                  </Typography>
                  <ExpandableCell value={currentVisit.review} isHtml />
                </Paper>
              ) : null}

              {/* 6. CAPTURED MEDIA & PHOTOS GALLERY */}
              {currentVisit.media && currentVisit.media.length > 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 3,
                    bgcolor: '#ffffff',
                    borderColor: '#e2e8f0',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#334155', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PhotoCameraOutlinedIcon sx={{ fontSize: 18, color: '#059669' }} /> Captured Visit Photos & Media ({currentVisit.media.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {currentVisit.media.map((m) => (
                      <Grid item xs={6} sm={4} md={3} key={m.id}>
                        <Box
                          component="a"
                          href={m.file_url || m.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'block',
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid #e2e8f0',
                            textDecoration: 'none',
                            bgcolor: '#f8fafc',
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'scale(1.02)', borderColor: '#059669', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
                          }}
                        >
                          <Box
                            component="img"
                            src={m.file_url || m.file}
                            alt={m.caption || 'Visit Photo'}
                            sx={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }}
                          />
                          {m.caption && (
                            <Typography variant="caption" sx={{ p: 1, display: 'block', color: '#334155', fontWeight: 600 }}>
                              {m.caption}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              ) : null}

              {/* 7. POSTPONED / RESCHEDULE HISTORY (IF APPLICABLE) */}
              {currentVisit.postponedDate && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: 2.5,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#b45309', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <EventRepeatOutlinedIcon sx={{ fontSize: 18 }} /> Reschedule Notice
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#92400e', fontWeight: 600 }}>
                    This visit was postponed to: {formatDate(currentVisit.postponedDate)} {currentVisit.postponedTime ? `at ${currentVisit.postponedTime}` : ''}
                  </Typography>
                </Paper>
              )}

              {/* 8. AUDIT TIMESTAMPS */}
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #e2e8f0', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', color: 'text.secondary' }}>
                {currentVisit.created && (
                  <Typography variant="caption">
                    Logged: {formatDateTime(currentVisit.created)}
                  </Typography>
                )}
                {currentVisit.modified && (
                  <Typography variant="caption">
                    Last modified: {formatDateTime(currentVisit.modified)}
                  </Typography>
                )}
              </Box>
            </DialogContent>

            {/* ── DIALOG FOOTER WITH NEXT & PREVIOUS BUTTONS ── */}
            <DialogActions
              sx={{
                p: 2,
                px: 3,
                bgcolor: '#f8fafc',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIosNewIcon sx={{ fontSize: '13px !important' }} />}
                disabled={selectedVisitIndex <= 0}
                onClick={() => setSelectedVisitIndex((i) => Math.max(0, i - 1))}
                sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
              >
                Previous Visit
              </Button>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  onClick={() => setSelectedVisitIndex(null)}
                  variant="outlined"
                  color="inherit"
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIosIcon sx={{ fontSize: '13px !important' }} />}
                  disabled={selectedVisitIndex >= displayVisits.length - 1}
                  onClick={() => setSelectedVisitIndex((i) => Math.min(displayVisits.length - 1, i + 1))}
                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                >
                  Next Visit
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default function VisitSummaryTab(props) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 15);
  const defaultEndDate = new Date();
  defaultEndDate.setDate(defaultEndDate.getDate() + 15);

  const formatDateForInput = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [startDate, setStartDate] = useState(props.companyId ? '' : formatDateForInput(defaultStartDate));
  const [endDate, setEndDate] = useState(props.companyId ? '' : formatDateForInput(defaultEndDate));
  const [statusFilter, setStatusFilter] = useState('all');

  const loadRows = () => {
    setLoading(true);

    const params = {
      limit: 1000,
      pageno: 1,
    };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (props.companyId) params.company = props.companyId;
    if (props.mine) params.mine = '1';

    getRequest(GET_URL.company_visits.api, params, props)
      .then((response) => {
        if (response && response.status === 200) {
          const data = response.data?.data || [];
          setRows(Array.isArray(data) ? data : []);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line
  }, [startDate, endDate, statusFilter, props.companyId]);

  return (
    <Box sx={{ mt: 3 }}>
      {!props.companyId && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '16px', boxShadow: '0 6px 20px rgba(0,0,0,0.05)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <DateRange
                label="Visit Date Range"
                startDate={startDate}
                endDate={endDate}
                handleChange={(val) => {
                  if (val && val.start && val.end) {
                    setStartDate(val.start);
                    setEndDate(val.end);
                  } else {
                    setStartDate('');
                    setEndDate('');
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                variant="outlined"
                size="small"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="visited">Visited</MenuItem>
                <MenuItem value="postponed">Postponed</MenuItem>
                <MenuItem value="cancel">Canceled</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      )}

      <VisitSummaryView rows={rows} loading={loading} />
    </Box>
  );
}
